'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
    quote: string;
    author: string;
    role: string;
    company: string;
    avatar: string;
    stat: string;
    statLabel: string;
}

const testimonials: Testimonial[] = [
    {
        quote:
            'CapState changed how I debug. I stopped dreading refactors because I know I can restore any function to exactly how it was — not the whole file, just that one block.',
        author: 'Priya Venkataraman',
        role: 'Senior Engineer',
        company: 'Razorpay',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9088244-1785220854167.png",
        stat: '3.2h',
        statLabel: 'saved per week'
    },
    {
        quote:
            'We were losing hours reconstructing files after reverting. CapState gave us block-level precision. We shipped the payment refactor in half the time.',
        author: 'Marcus Webb',
        role: 'Lead Developer',
        company: 'Stripe',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b1f355b9-1772159790022.png",
        stat: '47%',
        statLabel: 'faster recovery'
    },
    {
        quote:
            'The CLI is exactly what I wanted. capstate restore functionName@timestamp — that\'s it. No UI friction. It fits the way I already work.',
        author: 'Yuki Tanaka',
        role: 'Staff Engineer',
        company: 'Vercel',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e7eabec-1784871019266.png",
        stat: '8s',
        statLabel: 'avg restore time'
    }];


const stats = [
    { value: '12+', label: 'Active developers' },
    { value: '250+', label: 'Blocks tracked' },
    { value: '99.8%', label: 'Restore accuracy' },
    { value: '< 10s', label: 'Avg recovery time' }];


export default function TestimonialsSection() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [words, setWords] = useState<string[]>([]);
    const [animating, setAnimating] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const wordsRef = useRef<HTMLSpanElement[]>([]);

    const updateTestimonial = useCallback((index: number) => {
        setAnimating(true);
        setTimeout(() => {
            setActiveIndex(index);
            setWords(testimonials[index].quote.split(' '));
            setAnimating(false);
        }, 200);
    }, []);

    useEffect(() => {
        setWords(testimonials[0].quote.split(' '));
    }, []);

    const handleNext = () => {
        updateTestimonial((activeIndex + 1) % testimonials.length);
    };

    const current = testimonials[activeIndex];
    const progressWidth = (activeIndex + 1) / testimonials.length * 100;

    return (
        <section id="testimonials" ref={sectionRef} className="py-24 px-6 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 wireframe-bg opacity-50 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] blob-green opacity-30 pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
                    {stats.map((stat, i) =>
                        <div
                            key={i}
                            className="bento-card text-center"
                            style={{ animationDelay: `${i * 100}ms` }}>

                            <p className="text-3xl md:text-4xl font-extrabold text-foreground tracking-tight mb-1">
                                {stat.value.includes('k') || stat.value.includes('M') || stat.value.includes('%') ?
                                    <>
                                        <span className="text-gradient-primary">{stat.value}</span>
                                    </> :

                                    <span className="text-gradient-accent">{stat.value}</span>
                                }
                            </p>
                            <p className="font-mono-code text-xs text-muted-foreground uppercase tracking-wider">
                                {stat.label}
                            </p>
                        </div>
                    )}
                </div>

                {/* Testimonial */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    {/* Left: Testimonial text */}
                    <div>
                        <p className="section-label mb-6">What developers say</p>

                        {/* Avatar previews */}
                        <div className="flex items-center gap-2 mb-8">
                            {testimonials.map((t, i) =>
                                <button
                                    key={i}
                                    onClick={() => updateTestimonial(i)}
                                    className={`rounded-full border-2 overflow-hidden transition-all duration-300 cursor-pointer ${i === activeIndex ?
                                        'w-10 h-10 border-primary shadow-[0_0_12px_rgba(232,255,71,0.4)]' :
                                        'w-7 h-7 border-border opacity-50 hover:opacity-80'}`
                                    }
                                    aria-label={`View testimonial from ${t.author}`}>

                                    <AppImage
                                        src={t.avatar}
                                        alt={`${t.author}, ${t.role} at ${t.company}`}
                                        width={40}
                                        height={40}
                                        className="w-full h-full object-cover" />

                                </button>
                            )}
                            <span className="font-mono-code text-xs text-muted-foreground ml-2">
                                {String(activeIndex + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
                            </span>
                        </div>

                        {/* Quote */}
                        <blockquote
                            className={`text-xl md:text-2xl font-light text-foreground leading-relaxed mb-8 transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`
                            }
                            style={{ minHeight: '8rem' }}>

                            &ldquo;{current.quote}&rdquo;
                        </blockquote>

                        {/* Author */}
                        <div
                            className={`flex items-center gap-4 mb-8 transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`
                            }>

                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/40">
                                <AppImage
                                    src={current.avatar}
                                    alt={`${current.author}, ${current.role} at ${current.company}`}
                                    width={48}
                                    height={48}
                                    className="w-full h-full object-cover" />

                            </div>
                            <div className="border-l border-primary/40 pl-4">
                                <p className="font-bold text-foreground text-sm">{current.author}</p>
                                <p className="font-mono-code text-xs text-muted-foreground uppercase tracking-wider">
                                    {current.role} — {current.company}
                                </p>
                            </div>
                        </div>

                        {/* Progress + Next */}
                        <div className="flex items-center gap-4">
                            <div className="flex-1 progress-bar-track rounded-full">
                                <div
                                    className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progressWidth}%` }} />

                            </div>
                            <button
                                onClick={handleNext}
                                className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
                                aria-label="Next testimonial">

                                <Icon name="ArrowRightIcon" size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Right: Stat highlight card */}
                    <div className="bento-card glow-accent relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-48 h-48 blob-cyan opacity-40 pointer-events-none" />
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center">
                                    <Icon name="BoltIcon" size={18} className="text-accent" />
                                </div>
                                <p className="section-label">Impact in numbers</p>
                            </div>

                            <div
                                className={`transition-opacity duration-200 ${animating ? 'opacity-0' : 'opacity-100'}`}>

                                <p className="text-6xl md:text-7xl font-extrabold text-foreground tracking-tight mb-2">
                                    <span className="text-gradient-accent">{current.stat}</span>
                                </p>
                                <p className="text-muted-foreground text-lg mb-8">{current.statLabel}</p>
                            </div>

                            {/* Mini code snippet */}
                            <div className="bg-background rounded-xl border border-border p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="font-mono-code text-xs text-muted-foreground">terminal</span>
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 rounded-full bg-muted" />
                                        <div className="w-2 h-2 rounded-full bg-muted" />
                                        <div className="w-2 h-2 rounded-full bg-primary" />
                                    </div>
                                </div>
                                <div className="code-block space-y-1">
                                    <p>
                                        <span className="text-accent">$ </span>
                                        <span className="text-foreground/80">capstate restore calculatePrice@2:41pm</span>
                                    </p>
                                    <p className="text-primary">✓ Block restored in 8.2s</p>
                                    <p className="text-muted-foreground">✓ 847 other lines untouched</p>
                                    <p className="text-muted-foreground">
                                        <span className="animate-cursor-blink">▌</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}